import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, Download, LogOut, Mail, Phone, Archive, CheckCircle2, Trash2, Filter, X, Inbox, FolderOpen, Users, HelpCircle, BarChart3, Image as ImageIcon, FileText } from "lucide-react";
import { ContentEditor } from "@/components/admin/ContentEditor";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "rccd_admin_token";

const apiCall = async (path, options = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = "/adminportal";
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Request failed" }));
    throw new Error(typeof err.detail === "string" ? err.detail : "Request failed");
  }
  return res.status === 204 ? null : res.json();
};

// ---------- LOGIN ----------
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await apiCall("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Welcome back.");
      onLogin();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-paper flex items-center justify-center px-6 grain">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center bg-paper/95 px-4 py-2 rounded-sm mb-6">
            <img src="/assets/rccd-emblem.png" alt="" className="h-10 w-auto" />
          </div>
          <h1 className="font-display text-4xl tracking-[-0.02em]">RCCD Admin</h1>
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-paper/50 mt-3">A Distinguished Company · Console</p>
        </div>
        <form onSubmit={submit} className="space-y-5 bg-paper/[0.05] backdrop-blur-sm border border-paper/15 p-8">
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.22em] text-paper/60">Username</Label>
            <Input
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              className="mt-2 h-12 rounded-none border-0 border-b border-paper/30 bg-transparent px-0 text-base text-paper focus-visible:ring-0 focus-visible:border-clay"
            />
          </div>
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.22em] text-paper/60">Password</Label>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-2 h-12 rounded-none border-0 border-b border-paper/30 bg-transparent px-0 text-base text-paper focus-visible:ring-0 focus-visible:border-clay"
            />
          </div>
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 mt-6 rounded-none bg-clay text-paper hover:bg-primary-glow mono text-[11px] uppercase tracking-[0.25em]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
        <p className="mt-6 mono text-[10px] uppercase tracking-[0.22em] text-paper/30 text-center">
          Authorized personnel only
        </p>
      </div>
    </div>
  );
};

// ---------- DASHBOARD ----------
const SECTIONS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "projects", label: "Projects", icon: FolderOpen },
  { id: "team", label: "Team", icon: Users },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "stats", label: "Stats", icon: BarChart3 },
  { id: "partners", label: "Partners", icon: ImageIcon },
  { id: "copy", label: "Site copy", icon: FileText },
];

const Dashboard = ({ onLogout }) => {
  const [section, setSection] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_source: {}, by_status: {} });
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (section !== "leads") return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSource) params.set("source", filterSource);
      if (filterStatus) params.set("status", filterStatus);
      if (query) params.set("q", query);
      const [data, s] = await Promise.all([
        apiCall(`/api/admin/leads?${params}`),
        apiCall("/api/admin/leads/stats"),
      ]);
      setLeads(data);
      setStats(s);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterStatus, query, section]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await apiCall(`/api/admin/leads/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      toast.success(`Marked as ${status}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteLead = async (id) => {
    if (!confirm("Delete this lead permanently?")) return;
    try {
      await apiCall(`/api/admin/leads/${id}`, { method: "DELETE" });
      toast.success("Lead deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportCSV = () => {
    const headers = ["created_at", "source", "status", "name", "company", "email", "phone", "project_type", "county", "acres", "estimated_weeks", "message"];
    const rows = leads.map((l) =>
      headers.map((h) => {
        const v = l[h] ?? "";
        const s = String(v).replace(/"/g, '""');
        return `"${s}"`;
      }).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rccd-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusBadge = (status) => {
    const map = {
      new: "bg-clay/20 text-clay border-clay/40",
      replied: "bg-accent/20 text-accent border-accent/40",
      archived: "bg-muted text-muted-foreground border-border",
    };
    return map[status] || map.new;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-56 bg-ink text-paper sticky top-0 h-screen">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-paper/10">
          <img src="/assets/rccd-emblem.png" alt="" className="h-8 w-auto" />
          <div>
            <div className="font-display text-base leading-none">RCCD Admin</div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-paper/45 mt-1">Console</div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                data-testid={`nav-${s.id}`}
                className={`w-full flex items-center gap-3 px-5 py-3 mono text-[11px] uppercase tracking-[0.22em] border-l-2 transition-colors ${active ? 'border-clay text-clay bg-paper/[0.05]' : 'border-transparent text-paper/65 hover:text-paper hover:bg-paper/[0.03]'}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-paper/10">
          <Button onClick={onLogout} variant="ghost" className="w-full text-paper/85 hover:text-clay hover:bg-paper/[0.06] mono text-[10px] uppercase tracking-[0.22em] justify-start" data-testid="admin-logout-btn">
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile section switcher */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-ink text-paper flex items-center gap-2 px-4 h-14 border-b border-paper/10 overflow-x-auto">
        <img src="/assets/rccd-emblem.png" alt="" className="h-7 w-auto shrink-0" />
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} className={`shrink-0 px-3 h-9 mono text-[10px] uppercase tracking-[0.22em] ${section === s.id ? 'bg-clay text-paper' : 'text-paper/65'}`}>{s.label}</button>
        ))}
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        {section === "leads" ? (
          <LeadsView leads={leads} stats={stats} loading={loading} filterSource={filterSource} setFilterSource={setFilterSource} filterStatus={filterStatus} setFilterStatus={setFilterStatus} query={query} setQuery={setQuery} selected={selected} setSelected={setSelected} updateStatus={updateStatus} deleteLead={deleteLead} exportCSV={exportCSV} statusBadge={statusBadge} />
        ) : (
          <div className="p-6 lg:p-10 max-w-[1100px]">
            <ContentEditor slug={section} key={section} />
          </div>
        )}
      </main>
    </div>
  );
};

// ---------- LEADS VIEW (extracted) ----------
const LeadsView = ({ leads, stats, loading, filterSource, setFilterSource, filterStatus, setFilterStatus, query, setQuery, selected, setSelected, updateStatus, deleteLead, exportCSV, statusBadge }) => (
  <div className="p-6 lg:p-10 max-w-[1400px]">
    {/* Stats */}
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-10">
      {[
        { label: "Total leads", value: stats.total, accent: "clay" },
        { label: "Contact", value: stats.by_source?.contact ?? 0 },
        { label: "Estimator", value: stats.by_source?.estimator ?? 0 },
        { label: "New", value: stats.by_status?.new ?? 0 },
        { label: "Replied", value: stats.by_status?.replied ?? 0 },
      ].map((s) => (
        <div key={s.label} className="bg-card border border-border p-5">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{s.label}</div>
          <div className={`font-display text-3xl lg:text-4xl mt-2 ${s.accent === "clay" ? "text-clay" : "text-ink"}`}>{s.value}</div>
        </div>
      ))}
    </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company, message..."
              className="pl-10 h-11 rounded-none border-border bg-card"
            />
          </div>
          <select
            data-testid="admin-source-filter"
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="h-11 px-3 rounded-none border border-border bg-card mono text-[11px] uppercase tracking-[0.18em]"
          >
            <option value="">All sources</option>
            <option value="contact">Contact</option>
            <option value="estimator">Estimator</option>
          </select>
          <select
            data-testid="admin-status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-11 px-3 rounded-none border border-border bg-card mono text-[11px] uppercase tracking-[0.18em]"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="replied">Replied</option>
            <option value="archived">Archived</option>
          </select>
          <Button onClick={exportCSV} variant="outline" className="h-11 rounded-none border-border bg-card mono text-[11px] uppercase tracking-[0.22em]" data-testid="admin-export-csv">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
        </div>

        {/* Lead table */}
        <div className="bg-card border border-border overflow-x-auto">
          {loading ? (
            <div className="p-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading...</div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-muted-foreground">No leads match your filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border">
                <tr>
                  {["Date", "Source", "Status", "Name", "Company", "Email", "Phone", ""].map((h) => (
                    <th key={h} className="text-left mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer transition-colors" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-4 mono text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-4 mono text-[10px] uppercase tracking-[0.18em] text-clay">{l.source}</td>
                    <td className="px-4 py-4"><Badge variant="outline" className={`rounded-none mono text-[9px] uppercase tracking-[0.2em] ${statusBadge(l.status)}`}>{l.status || "new"}</Badge></td>
                    <td className="px-4 py-4 font-medium text-ink">{l.name || "—"}</td>
                    <td className="px-4 py-4 text-foreground/75">{l.company || "—"}</td>
                    <td className="px-4 py-4 text-foreground/75">{l.email || "—"}</td>
                    <td className="px-4 py-4 text-foreground/75">{l.phone || "—"}</td>
                    <td className="px-4 py-4 text-right text-clay">→</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="mt-6 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Showing {leads.length} {leads.length === 1 ? "lead" : "leads"} · Click any row for full detail
        </p>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-ink/70 backdrop-blur-sm flex justify-end" onClick={() => setSelected(null)}>
          <aside onClick={(e) => e.stopPropagation()} className="bg-paper text-ink w-full max-w-xl h-full overflow-y-auto p-8 lg:p-10 border-l-4 border-clay" data-testid="lead-detail">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-clay">{selected.source}</div>
                <h2 className="font-display text-3xl mt-2 tracking-[-0.01em]">{selected.name || "Unnamed lead"}</h2>
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-2">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-clay"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-5 text-sm">
              {selected.company && (<Row label="Company" value={selected.company} />)}
              {selected.email && (<Row label="Email" value={<a href={`mailto:${selected.email}`} className="text-clay hover:underline">{selected.email}</a>} />)}
              {selected.phone && (<Row label="Phone" value={<a href={`tel:${selected.phone}`} className="text-clay hover:underline">{selected.phone}</a>} />)}
              {selected.project_type && (<Row label="Project type" value={selected.project_type} />)}
              {selected.county && (<Row label="Jurisdiction" value={selected.county} />)}
              {selected.acres != null && (<Row label="Acres" value={selected.acres} />)}
              {selected.terrain && (<Row label="Status / terrain" value={selected.terrain} />)}
              {selected.phases?.length > 0 && (<Row label="Scope phases" value={selected.phases.join(", ")} />)}
              {selected.estimated_weeks != null && (<Row label="Indicative timeline" value={`~${selected.estimated_weeks} weeks`} />)}
              {selected.estimated_total != null && (<Row label="Estimated total" value={`$${Number(selected.estimated_total).toLocaleString()}`} />)}
              {selected.message && (
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Message</div>
                  <div className="mt-2 p-4 bg-secondary border border-border whitespace-pre-wrap text-foreground/85">{selected.message}</div>
                </div>
              )}
            </div>

            <div className="mt-10 pt-6 border-t border-border space-y-3">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Status</div>
              <div className="grid grid-cols-3 gap-2">
                <Button onClick={() => updateStatus(selected.id, "new")} variant={selected.status === "new" ? "default" : "outline"} className="rounded-none mono text-[10px] uppercase tracking-[0.22em]" data-testid="status-new"><Mail className="h-3.5 w-3.5 mr-1" />New</Button>
                <Button onClick={() => updateStatus(selected.id, "replied")} variant={selected.status === "replied" ? "default" : "outline"} className="rounded-none mono text-[10px] uppercase tracking-[0.22em]" data-testid="status-replied"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Replied</Button>
                <Button onClick={() => updateStatus(selected.id, "archived")} variant={selected.status === "archived" ? "default" : "outline"} className="rounded-none mono text-[10px] uppercase tracking-[0.22em]" data-testid="status-archived"><Archive className="h-3.5 w-3.5 mr-1" />Archive</Button>
              </div>
              {selected.email && (
                <Button asChild className="w-full mt-3 rounded-none bg-clay text-paper hover:bg-primary-glow mono text-[10px] uppercase tracking-[0.22em]">
                  <a href={`mailto:${selected.email}?subject=${encodeURIComponent('Following up on your inquiry to RCCD')}&body=${encodeURIComponent(`Hi ${selected.name || ''},\n\nThank you for reaching out to RCCD. I'm following up on your inquiry${selected.project_type ? ' regarding your ' + selected.project_type + ' project' : ''}.\n\nBest regards,\nRCCD Team\n(714) 637-4405`)}`}>
                    <Mail className="h-3.5 w-3.5 mr-2" /> Reply via email
                  </a>
                </Button>
              )}
              <Button onClick={() => deleteLead(selected.id)} variant="ghost" className="w-full mt-1 text-destructive hover:bg-destructive/10 mono text-[10px] uppercase tracking-[0.22em]" data-testid="delete-lead-btn">
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete permanently
              </Button>
            </div>

            <div className="mt-10 pt-6 border-t border-border mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60">
              Lead ID: {selected.id}
            </div>
          </aside>
        </div>
      )}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex gap-6 items-baseline border-b border-border/60 pb-3">
    <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-32 shrink-0">{label}</div>
    <div className="text-ink text-base">{value}</div>
  </div>
);

// ---------- ROOT ----------
export default function AdminPortal() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setAuthed(false);
      return;
    }
    apiCall("/api/auth/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-paper/50" /></div>;
  }
  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  return <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} />;
}
